import { SectionTitle } from "@/components/shared/SectionTitle";
import { DocToc } from "@/components/docs/DocToc";
import { DocCallout } from "@/components/docs/DocCallout";
import { DocFormula } from "@/components/docs/DocFormula";
import { DocTable } from "@/components/docs/DocTable";
import { RarityBadge } from "@/components/shared/RarityBadge";
import { AddressCopy } from "@/components/shared/AddressCopy";
import { ADDRESSES } from "@/lib/contracts";

const tocItems = [
  { id: "positioning", label: "1. Positioning" },
  { id: "loop", label: "2. Core Loop" },
  { id: "mint", label: "3. NFT Mint" },
  { id: "rarity", label: "4. Rarity & Base Multipliers" },
  { id: "burn", label: "5. Burn Upgrades" },
  { id: "fuse", label: "6. Fusion Mechanic" },
  { id: "revenue", label: "7. Revenue & Dividends" },
  { id: "vault", label: "8. Vault & Withdrawal" },
  { id: "dormant", label: "9. Resale & Dormancy" },
  { id: "tokens", label: "10. Token Economy" },
  { id: "contracts", label: "11. Contract Addresses" },
  { id: "params", label: "12. Parameter Summary" },
  { id: "risks", label: "13. Risk Notes" },
];

export default function DocsPage() {
  return (
    <div className="min-h-screen bg-background py-16 text-foreground">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <SectionTitle
          subtitle="Cat Claw · Gameplay Rules"
          title="Gameplay Docs"
          description="Pixel cats with stock tokens inside. An NFT-Fi project on Robinhood Chain · 1,024 pixel cats · holding NVDA/SPCX/SPY stock tokens."
          centered
        />

        <p className="mb-8 text-lg leading-relaxed text-muted-foreground">
          Cat Claw combines a pixel cat NFT with an on-chain stock vault. Each cat is a PFP and a &quot;stock wallet&quot;. Players upgrade multipliers by burning $CATCLAW, fuse multiple cats, and split platform fees converted into stock rewards.
        </p>

        <DocToc items={tocItems} />

        <section id="positioning" className="mb-14">
          <h2 className="mb-5 inline-block border-b-2 border-primary pb-2 text-2xl font-bold sm:text-3xl">
            1. Positioning
          </h2>
          <p className="mb-4 leading-relaxed">
            Cat Claw = <strong>pixel cat PFP + built-in stock vault + burn upgrades + fusion game theory</strong>.
          </p>
          <p className="mb-4 leading-relaxed">
            Unlike pure collectibles, every Cat Claw cat has base value: it holds{" "}
            <strong>NVDA, SPCX, SPY</strong> stock tokens inside. Whoever owns the cat owns those stock tokens.
          </p>
          <DocCallout title="One-liner Summary" variant="blue">
            <p>Buying a cat = buying a pixel asset package that auto-accumulates stock tokens; burning tokens to upgrade = increasing dividend rights; fusion = synthesizing a higher-weight cat.</p>
          </DocCallout>
          <DocCallout title="Important Note" variant="red">
            <p>
              NVDA/SPCX/SPY in Cat Claw are <strong>tokenized stock tokens on Robinhood Chain</strong>. They represent price exposure to the corresponding assets and{" "}
              <strong>do not confer real equity, voting rights, or dividend rights</strong>. They are tradable on-chain assets, but not real stocks.
            </p>
          </DocCallout>
        </section>

        <section id="loop" className="mb-14">
          <h2 className="mb-5 inline-block border-b-2 border-primary pb-2 text-2xl font-bold sm:text-3xl">
            2. Core Loop
          </h2>
          <ol className="list-decimal space-y-2 pl-5 leading-relaxed">
            <li><strong>Mint</strong>: pay 0.0025 ETH and receive a cat of random rarity</li>
            <li><strong>Burn</strong> $CATCLAW: activate the cat or upgrade its multiplier</li>
            <li><strong>Earn</strong>: platform fees are auto-swapped to NVDA/SPCX/SPY and distributed to your cats by multiplier</li>
            <li><strong>Fuse</strong>: merge multiple cats into one higher-weight cat</li>
            <li><strong>Withdraw</strong>: pull the stock tokens from the cat&apos;s belly into your wallet</li>
          </ol>
        </section>

        <section id="mint" className="mb-14">
          <h2 className="mb-5 inline-block border-b-2 border-primary pb-2 text-2xl font-bold sm:text-3xl">
            3. NFT Mint
          </h2>
          <DocTable
            headers={["Parameter", "Value"]}
            rows={[
              ["Total Supply", "1,024"],
              ["Mint Price", "0.0025 ETH"],
              ["Max per Wallet", "5"],
              ["Chain", "Robinhood Chain"],
            ]}
            monoColumns={[1]}
          />
          <p className="leading-relaxed">
            Mint fees flow into the platform fee pool, where <strong>80%</strong> is later returned to active cat holders as stock tokens, and <strong>20%</strong> covers operations and keeper gas.
          </p>
        </section>

        <section id="rarity" className="mb-14">
          <h2 className="mb-5 inline-block border-b-2 border-primary pb-2 text-2xl font-bold sm:text-3xl">
            4. Rarity & Base Multipliers
          </h2>
          <p className="mb-4 leading-relaxed">
            Each cat receives a random rarity at mint. Rarity determines its <strong>innate</strong> revenue weight base. Higher rarity means more dividends for the same burn investment.
          </p>
          <DocTable
            headers={["Rarity", "Share", "Base Multiplier", "Contract Weight"]}
            rows={[
              [<RarityBadge key="common" rarity="common" />, "70%", "1.00×", "100"],
              [<RarityBadge key="rare" rarity="rare" />, "25%", "1.15×", "115"],
              [<RarityBadge key="legendary" rarity="legendary" />, "4%", "1.35×", "135"],
              [<RarityBadge key="mythic" rarity="mythic" />, "1%", "1.60×", "160"],
            ]}
            monoColumns={[2, 3]}
          />
          <DocFormula>Final revenue weight = base multiplier × burn tier multiplier / 100</DocFormula>
        </section>

        <section id="burn" className="mb-14">
          <h2 className="mb-5 inline-block border-b-2 border-primary pb-2 text-2xl font-bold sm:text-3xl">
            5. Burn Upgrades
          </h2>
          <p className="mb-4 leading-relaxed">
            After mint, a cat is in a <strong>dormant state</strong> and cannot participate in dividends. You must burn <strong>$CATCLAW</strong> to activate it and increase its multiplier. Burned tokens are permanently destroyed.
          </p>
          <DocTable
            headers={["Stage", "Total Burned $CATCLAW", "Tier Multiplier"]}
            rows={[
              ["Asleep (Dormant)", "0", "0×"],
              ["Active", "25,000", "1×"],
              ["Climb I", "75,000", "1.4×"],
              ["Climb II", "150,000", "1.9×"],
              ["Climb III", "300,000", "2.5×"],
              ["Alpha Burn (Max)", "850,000", "3.5×"],
            ]}
            monoColumns={[1, 2]}
          />
          <DocCallout title="Example" variant="green">
            <p>
              A <strong>Rare</strong> cat upgraded to Alpha Burn: 115 × 350 / 100 = <strong>403 weight</strong>. A <strong>Mythic</strong> cat at the same tier: 160 × 350 / 100 = <strong>560 weight</strong>.
            </p>
          </DocCallout>
        </section>

        <section id="fuse" className="mb-14">
          <h2 className="mb-5 inline-block border-b-2 border-primary pb-2 text-2xl font-bold sm:text-3xl">
            6. Fusion Mechanic
          </h2>
          <p className="mb-4 leading-relaxed">
            Players can fuse multiple cats into one stronger cat. The fused cats are burned, and their vault assets and multipliers are fully merged into the &quot;survivor&quot; cat.
          </p>
          <DocTable
            headers={["Operation", "Extra Burn $CATCLAW", "Weight Calculation"]}
            rows={[
              ["Fuse 2 cats", "50,000", "(A + B) × 1.2"],
              ["Add a 3rd cat", "+100,000", "(A + B + C) × 1.3"],
            ]}
            monoColumns={[1, 2]}
          />
          <DocCallout title="Key Rules" variant="red">
            <p>
              <strong>Fusion does not generate a new image.</strong> Fused NFTs are destroyed directly, and the survivor cat&apos;s image and metadata remain unchanged. Fusion only changes weight and vault balance.
            </p>
          </DocCallout>
        </section>

        <section id="revenue" className="mb-14">
          <h2 className="mb-5 inline-block border-b-2 border-primary pb-2 text-2xl font-bold sm:text-3xl">
            7. Revenue & Dividends
          </h2>
          <h3 className="mb-3 mt-6 text-lg font-semibold text-foreground">Revenue Sources</h3>
          <ul className="mb-5 list-disc space-y-2 pl-5 leading-relaxed">
            <li>Mint fees (0.0025 ETH × 1,024)</li>
            <li>Secondary market trading fees (if a marketplace is built)</li>
            <li>Future protocol revenue streams</li>
          </ul>
          <h3 className="mb-3 text-lg font-semibold text-foreground">Distribution</h3>
          <DocTable
            headers={["Destination", "Ratio", "Description"]}
            rows={[
              ["Active Holder Reward Pool", "80%", "Auto-swapped to NVDA + SPCX + SPY every hour / round"],
              ["Operations & Keeper Gas", "20%", "Team, frontend, and keeper bot maintenance"],
            ]}
            monoColumns={[1]}
          />
          <h3 className="mb-3 text-lg font-semibold text-foreground">Dividend Formula</h3>
          <DocFormula>Single cat single-round dividend = (cat weight / total active cat weight) × round reward pool</DocFormula>
        </section>

        <section id="vault" className="mb-14">
          <h2 className="mb-5 inline-block border-b-2 border-primary pb-2 text-2xl font-bold sm:text-3xl">
            8. Vault & Withdrawal
          </h2>
          <p className="mb-4 leading-relaxed">
            Every cat has a built-in vault. Dividends are not sent directly to your wallet; they are <strong>recorded by tokenId</strong> and stored in that cat&apos;s vault.
          </p>
          <ul className="list-disc space-y-2 pl-5 leading-relaxed">
            <li><strong>Moves with the cat</strong>: when selling the cat, the stock tokens in the vault go to the buyer</li>
            <li><strong>Only holder can withdraw</strong>: only the current ownerOf(tokenId) can withdraw</li>
            <li><strong>Zero-fee withdrawal</strong>: keeper pays gas via signed relay, or the user pays gas themselves</li>
            <li><strong>Single or full withdrawal</strong>: withdraw all three stock tokens at once</li>
          </ul>
        </section>

        <section id="dormant" className="mb-14">
          <h2 className="mb-5 inline-block border-b-2 border-primary pb-2 text-2xl font-bold sm:text-3xl">
            9. Resale & Dormancy
          </h2>
          <p className="mb-4 leading-relaxed">
            When a cat is transferred, it automatically enters a <strong>dormant state</strong>.
          </p>
          <ul className="mb-5 list-disc space-y-2 pl-5 leading-relaxed">
            <li>During dormancy it does not participate in dividends (its weight is excluded from the denominator)</li>
            <li>Accumulated multiplier, fusion weight, and vault assets are <strong>fully preserved</strong></li>
            <li>The new buyer must burn <strong>25,000 $CATCLAW</strong> to reactivate</li>
          </ul>
          <DocCallout title="Design Intent">
            <p>Resale dormancy continuously creates $CATCLAW burn demand, while protecting sellers from &quot;withdrawing before selling&quot; — once the cat changes hands, it stops earning yield until the new owner invests.</p>
          </DocCallout>
        </section>

        <section id="tokens" className="mb-14">
          <h2 className="mb-5 inline-block border-b-2 border-primary pb-2 text-2xl font-bold sm:text-3xl">
            10. Token Economy
          </h2>
          <h3 className="mb-3 mt-6 text-lg font-semibold text-foreground">$CATCLAW (Burn Token)</h3>
          <ul className="mb-5 list-disc space-y-2 pl-5 leading-relaxed">
            <li>Use: activate cats, upgrade multipliers, fuse</li>
            <li>Mechanism: permanently destroyed each time it is used (deflationary)</li>
            <li>Launch: fair launch via Pons/Flap launchpad</li>
          </ul>
          <h3 className="mb-3 text-lg font-semibold text-foreground">Reward Assets (Stock Tokens)</h3>
          <DocTable
            headers={["Asset", "Ticker", "Contract Address", "Role"]}
            rows={[
              ["NVIDIA", "NVDA", ADDRESSES.NVDA, "AI / chip growth stock"],
              ["SpaceX", "SPCX", ADDRESSES.SPCX, "Space exploration / Musk-concept stock"],
              ["SPDR S&P 500", "SPY", ADDRESSES.SPY, "Broad market stable base"],
            ]}
            monoColumns={[1, 2]}
          />
        </section>

        <section id="contracts" className="mb-14">
          <h2 className="mb-5 inline-block border-b-2 border-primary pb-2 text-2xl font-bold sm:text-3xl">
            11. Contract Addresses
          </h2>
          <p className="mb-4 leading-relaxed">
            All contracts are deployed on <strong>Robinhood Chain (chainId 4663)</strong> and verified on Blockscout.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <AddressCopy label="$CATCLAW" address={ADDRESSES.CATCLAW} />
            <AddressCopy label="Cat Claw NFT" address={ADDRESSES.CatStonks} />
            <AddressCopy label="Cat Claw Rounds" address={ADDRESSES.CatStonksRounds} />
            <AddressCopy label="Cat Claw Vault" address={ADDRESSES.CatStonksVault} />
            <AddressCopy label="NVDA Stock Token" address={ADDRESSES.NVDA} />
            <AddressCopy label="SPCX Stock Token" address={ADDRESSES.SPCX} />
            <AddressCopy label="SPY Stock Token" address={ADDRESSES.SPY} />
            <AddressCopy label="WETH" address={ADDRESSES.WETH} />
            <AddressCopy label="Uniswap V3 Router" address={ADDRESSES.Router} />
            <AddressCopy label="Treasury / Operator" address={ADDRESSES.Treasury} />
          </div>
        </section>

        <section id="params" className="mb-14">
          <h2 className="mb-5 inline-block border-b-2 border-primary pb-2 text-2xl font-bold sm:text-3xl">
            12. Parameter Summary
          </h2>
          <DocTable
            headers={["Parameter", "Value"]}
            rows={[
              ["Project / NFT series name", "Cat Claw"],
              ["Burn token", "$CATCLAW"],
              ["Total supply", "1,024"],
              ["Mint price", "0.0025 ETH"],
              ["Max per wallet", "5"],
              ["Target chain", "Robinhood Chain"],
              ["Revenue split", "80% holders / 20% operations"],
              ["Reward assets", "NVDA + SPCX + SPY"],
              ["Visual fusion", "No"],
            ]}
            monoColumns={[1]}
          />
        </section>

        <section id="risks" className="mb-14">
          <h2 className="mb-5 inline-block border-b-2 border-primary pb-2 text-2xl font-bold sm:text-3xl">
            13. Risk Notes
          </h2>
          <ul className="mb-5 list-disc space-y-2 pl-5 leading-relaxed">
            <li><strong>Reward token price volatility</strong>: NVDA, SPCX, and SPY prices fluctuate, so the dividend value of each cat fluctuates</li>
            <li><strong>$CATCLAW burn is irreversible</strong>: burned tokens disappear forever</li>
            <li><strong>Liquidity risk</strong>: if secondary market prices are lower than expected, returns may not cover costs</li>
            <li><strong>Keeper dependency</strong>: dividends require an off-chain bot to trigger; bot downtime delays dividends but does not move funds</li>
          </ul>
          <DocCallout title="Special Reminder" variant="red">
            <p>Stock tokens are a real on-chain mapping of stock prices, but are not equivalent to equity.</p>
          </DocCallout>
        </section>

        <div className="my-12 h-px bg-border" />

        <footer className="text-xs text-muted-foreground">
          <p className="mb-2">Doc version: 2026-08-14 · Cat Claw design locked</p>
          <p>This document is for internal alignment and community explanation only, and does not constitute investment advice.</p>
        </footer>
      </div>
    </div>
  );
}
